import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import SdgIcon from '../../components/common/SdgIcon';
import PurposeStats from '../../components/common/PurposeStats';
import { purposeLabel, purposeShort } from '../../components/common/EventTags';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* The UN goals the company works towards.

   The 17 are a fixed list, so this is a set of toggles rather than a form. The chip commits the
   pick; the reason underneath is written after, and only for goals actually picked.

   ★ Picking a goal and writing why are two separate acts, deliberately. Demanding the reason
   first would leave a company that knows its goals but has not found the words having picked
   nothing at all.

   ★ The labels are translated and keyed on the official NUMBER (``cso_sdg<n>``), so the English
   names sitting in data.SDG are only ever the fallback — which is why the label helper is the
   shared ``purposeLabel`` and not a second copy of the same ternary. */

const CompanySdgs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/company/purpose')
      .then((res) => {
        setData(res);
        /* ★ ?item= — the way back in from /purpose/sdg/<number>. Addressed by the goal's official
           NUMBER, which is what the badge and the detail page both key on; the store wants the
           row's sdg_id, so the number is resolved against the picked list here. */
        const wanted = (params.get('item') || '').trim();
        if (wanted) {
          const found = (res.sdgs || []).find((g) => String(g.number) === wanted);
          if (found) setDraft({ sdg_id: found.sdg_id, reason: found.reason || '' });
        }
      })
      .catch(() => navigate('/member-home'));
  }, [navigate, params]);

  useEffect(() => { load(); }, [load]);

  const toggle = (goal, picked) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const send = picked
      ? apiRequest('DELETE', `/bravos/company/sdg/${goal.id}`)
      : apiRequest('POST', '/bravos/company/sdg', { sdg_id: goal.id });
    send
      .then((res) => {
        setData((d) => ({ ...d, sdgs: res.sdgs }));
        if (picked) setDraft((cur) => (cur && cur.sdg_id === goal.id ? null : cur));
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const saveReason = () => {
    if (busy || !draft) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/company/sdg', { sdg_id: draft.sdg_id, reason: draft.reason })
      .then((res) => {
        setData((d) => ({ ...d, sdgs: res.sdgs }));
        setDraft(null);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const sdgs = (data && data.sdgs) || [];
  const catalog = (data && data.sdg_catalog) || [];
  const picked = new Map(sdgs.map((g) => [String(g.sdg_id).toLowerCase(), g]));
  const label = (g) => purposeLabel(t, { ...g, kind: 'sdg' });

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="globe" theme="primary" className="me-2" />
          {t('ourSdgs')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('ourSdgsNote')}</p>

            <div className="cs-chip-row mb-3">
              {catalog.map((g) => {
                const on = picked.has(String(g.id).toLowerCase());
                return (
                  <button
                    type="button"
                    key={g.id}
                    className={`cs-chip ${on ? 'cs-chip--on' : ''}`}
                    /* ★ Each goal's official UN colour, off data.SDG's number. */
                    style={on && g.color
                      ? { borderColor: g.color, backgroundColor: g.color }
                      : undefined}
                    onClick={() => toggle(g, on)}
                    disabled={busy}
                  >
                    <SdgIcon number={g.number} className="cs-chip__sdg" />
                    {label(g)}
                  </button>
                );
              })}
            </div>

            {sdgs.length === 0 && <p className="ce-note">{t('noSdgsYet')}</p>}

            {sdgs.map((g) => (
              <div key={g.sdg_id} className="bravos-sdgrow">
                {/* The official tile in place of the number badge: it carries the number in its
                    own corner, in the artwork everyone already recognises. The coloured square is
                    the fallback for a goal the artwork does not cover. */}
                <SdgIcon number={g.number} className="bravos-sdgrow__icon" alt={label(g)} />
                {!g.number && (
                  <span
                    className="bravos-sdgrow__n"
                    style={g.color ? { background: g.color, color: '#fff' } : undefined}
                  >
                    {g.number}
                  </span>
                )}
                <span className="bravos-themerow__body">
                  <span className="bravos-themerow__name">{purposeShort(label(g))}</span>
                  <span className="bravos-themerow__when">{g.reason || t('sdgNoReason')}</span>
                </span>
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  onClick={() => setDraft({ sdg_id: g.sdg_id, reason: g.reason || '' })}
                >
                  {g.reason ? t('edit') : t('addReason')}
                </button>
              </div>
            ))}

            {draft && (
              <div className="bravos-form">
                <textarea
                  rows={3}
                  maxLength={2500}
                  value={draft.reason}
                  placeholder={t('sdgReasonPlaceholder')}
                  onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                />
                <div className="bravos-form__row">
                  <button type="button" className="bravos-submit"
                          onClick={saveReason} disabled={busy}>
                    {busy ? t('pleaseWait') : t('save')}
                  </button>
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => setDraft(null)}>
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      <Card>
        <Title my="1">{t('purposeStatsTitle')}</Title>
        <PurposeStats kind="sdg" />
      </Card>
    </Base>
  );
};

export default CompanySdgs;
